import React from "react";

// Layout: visual left, headline + copy right.
// Left visual: three orbit rings (12 icons total, varied sizes, colorful)
// over a decorative background of faint arcs and scattered track-dots,
// echoing the reference image's linework. Each icon still orbits its ring
// AND spins on its own axis independently.

const TEAL = "#0AB1B9";
const TEAL_DEEP = "#127A80";
const ORANGE = "#FF8A3D";
const AMBER = "#E8A33D";
const PURPLE = "#8B7CF6";
const PINK = "#F0637B";
const NAVY = "#0A2A2E";

const ICONS = [
  // inner ring
  { type: "palette", color: TEAL, ring: "inner", angle: -90, spin: 7, spinDir: 1, size: 58 },
  { type: "browser", color: ORANGE, ring: "inner", angle: -18, spin: 9, spinDir: -1, size: 44 },
  { type: "code", color: PURPLE, ring: "inner", angle: 54, spin: 6.5, spinDir: 1, size: 64 },
  { type: "git", color: AMBER, ring: "inner", angle: 126, spin: 8, spinDir: -1, size: 38 },
  { type: "terminal", color: PINK, ring: "inner", angle: 198, spin: 7.5, spinDir: 1, size: 50 },
  // mid ring
  { type: "server", color: TEAL_DEEP, ring: "mid", angle: -45, spin: 10, spinDir: -1, size: 46 },
  { type: "cloud", color: ORANGE, ring: "mid", angle: 45, spin: 8.5, spinDir: 1, size: 60 },
  { type: "db", color: PURPLE, ring: "mid", angle: 135, spin: 9.5, spinDir: -1, size: 40 },
  { type: "shield", color: TEAL, ring: "mid", angle: 225, spin: 7, spinDir: 1, size: 52 },
  // outer ring
  { type: "cpu", color: AMBER, ring: "outer", angle: -30, spin: 11, spinDir: -1, size: 48 },
  { type: "rocket", color: PINK, ring: "outer", angle: 90, spin: 9, spinDir: 1, size: 62 },
  { type: "phone", color: TEAL_DEEP, ring: "outer", angle: 210, spin: 10.5, spinDir: -1, size: 42 },
];

const RADIUS = { inner: 92, mid: 152, outer: 208 };
const ORBIT_DURATION = { inner: 24, mid: 32, outer: 42 };
const ORBIT_DIR = { inner: 1, mid: -1, outer: 1 };

// scattered background dots: {x, y, r, style: 'ring' | 'dot'}
const BG_DOTS = [
  { x: 60, y: 90, r: 5, style: "ring" },
  { x: 130, y: 40, r: 3, style: "dot" },
  { x: 330, y: 60, r: 4.5, style: "ring" },
  { x: 400, y: 130, r: 3, style: "dot" },
  { x: 415, y: 235, r: 5, style: "ring" },
  { x: 380, y: 340, r: 3, style: "dot" },
  { x: 290, y: 405, r: 4.5, style: "ring" },
  { x: 150, y: 420, r: 3, style: "dot" },
  { x: 45, y: 355, r: 4.5, style: "ring" },
  { x: 25, y: 245, r: 3, style: "dot" },
  { x: 95, y: 165, r: 2.6, style: "dot" },
  { x: 355, y: 175, r: 2.6, style: "dot" },
];

// decorative background arcs: partial circles at varying radii/spans
const BG_ARCS = [
  { r: 128, start: -40, end: 160, color: "rgba(245,247,247,0.10)" },
  { r: 178, start: 30, end: 260, color: "rgba(10,177,185,0.12)" },
  { r: 232, start: -120, end: 70, color: "rgba(245,247,247,0.07)" },
  { r: 200, start: 150, end: 340, color: "rgba(255,138,61,0.10)" },
];

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  const [x1, y1] = polar(cx, cy, r, startDeg);
  const [x2, y2] = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

function BackgroundArt({ size }) {
  const c = size / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" style={{ position: "absolute", inset: 0 }} aria-hidden="true">
      {BG_ARCS.map((a, i) => (
        <path key={i} d={arcPath(c, c, a.r, a.start, a.end)} fill="none" stroke={a.color} strokeWidth="1" />
      ))}
      {BG_DOTS.map((d, i) =>
        d.style === "ring" ? (
          <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="none" stroke="rgba(245,247,247,0.25)" strokeWidth="1.4" />
        ) : (
          <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="rgba(245,247,247,0.22)" />
        )
      )}
    </svg>
  );
}

function Glyph({ type, color }) {
  const c = { fill: "none", stroke: color, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg viewBox="0 0 24 24" width="52%" height="52%">
      {type === "code" && <path {...c} d="M8 6 3 12l5 6M16 6l5 6-5 6M14 4l-4 16" />}
      {type === "browser" && (
        <>
          <rect {...c} x="2.5" y="4" width="19" height="16" rx="2" />
          <path {...c} d="M2.5 8.5h19" />
          <circle cx="5.2" cy="6.2" r="0.6" fill={color} stroke="none" />
          <circle cx="7.2" cy="6.2" r="0.6" fill={color} stroke="none" />
        </>
      )}
      {type === "palette" && (
        <>
          <path {...c} d="M12 3a9 9 0 1 0 0 18c1.4 0 2-1 2-2s-.6-1.3-.6-2 .5-1.3 1.6-1.3H17a4 4 0 0 0 4-4c0-4.4-4-8.7-9-8.7Z" />
          <circle cx="7.5" cy="10.5" r="1.1" fill={color} stroke="none" />
          <circle cx="9.5" cy="7" r="1.1" fill={color} stroke="none" />
          <circle cx="14.5" cy="7" r="1.1" fill={color} stroke="none" />
        </>
      )}
      {type === "git" && (
        <>
          <circle {...c} cx="6" cy="6" r="2" />
          <circle {...c} cx="6" cy="18" r="2" />
          <circle {...c} cx="18" cy="12" r="2" />
          <path {...c} d="M6 8v8M6 12c4 0 6-3 6-3" />
        </>
      )}
      {type === "terminal" && (
        <>
          <rect {...c} x="2" y="4" width="20" height="16" rx="2" />
          <path {...c} d="M6 9l4 3-4 3M13 15h5" />
        </>
      )}
      {type === "server" && (
        <>
          <rect {...c} x="3" y="4" width="18" height="6" rx="1.4" />
          <rect {...c} x="3" y="14" width="18" height="6" rx="1.4" />
          <circle cx="7" cy="7" r="0.9" fill={color} stroke="none" />
          <circle cx="7" cy="17" r="0.9" fill={color} stroke="none" />
        </>
      )}
      {type === "cloud" && <path {...c} d="M7 18h10a4 4 0 0 0 .3-8 5.5 5.5 0 0 0-10.6-1.6A4 4 0 0 0 7 18Z" />}
      {type === "db" && (
        <>
          <ellipse {...c} cx="12" cy="6" rx="7" ry="3" />
          <path {...c} d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
          <path {...c} d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
        </>
      )}
      {type === "shield" && <path {...c} d="M12 2.5 19.5 5.5V11c0 5-3.4 8.7-7.5 11-4.1-2.3-7.5-6-7.5-11V5.5Z" />}
      {type === "cpu" && (
        <>
          <rect {...c} x="6" y="6" width="12" height="12" rx="2" />
          <rect {...c} x="9" y="9" width="6" height="6" rx="1" />
          <path {...c} d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
        </>
      )}
      {type === "rocket" && (
        <>
          <path {...c} d="M12 2c3 2 5 6 5 10 0 2-1 4-2 5l-3 3-3-3c-1-1-2-3-2-5 0-4 2-8 5-10Z" />
          <circle cx="12" cy="10" r="1.6" fill={color} stroke="none" />
          <path {...c} d="M9 17l-2.5 4M15 17l2.5 4" />
        </>
      )}
      {type === "phone" && (
        <>
          <rect {...c} x="7" y="2" width="10" height="20" rx="2.2" />
          <path {...c} d="M11 19h2" />
        </>
      )}
    </svg>
  );
}

function OrbitIcon({ icon, index }) {
  const radius = RADIUS[icon.ring];
  const orbitDur = ORBIT_DURATION[icon.ring];
  const orbitDir = ORBIT_DIR[icon.ring];
  const orbitName = `orbit-${index}`;
  const spinName = `spin-${index}`;
  const endAngle = icon.angle + orbitDir * 360;
  const spinEnd = icon.spinDir * 360;

  return (
    <>
      <style>{`
        @keyframes ${orbitName} { from { transform: rotate(${icon.angle}deg); } to { transform: rotate(${endAngle}deg); } }
        @keyframes ${spinName} { from { transform: rotate(0deg); } to { transform: rotate(${spinEnd}deg); } }
      `}</style>
      <div style={{ position: "absolute", inset: 0, transformOrigin: "50% 50%", animation: `${orbitName} ${orbitDur}s linear infinite` }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: `translate(-50%, -50%) translateX(${radius}px)` }}>
          <div
            style={{
              animation: `${spinName} ${icon.spin}s linear infinite`,
              width: icon.size,
              height: icon.size,
              borderRadius: "50%",
              background: icon.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 6px 18px -6px ${icon.color}99`,
            }}
          >
            <Glyph type={icon.type} color="#0A1315" />
          </div>
        </div>
      </div>
    </>
  );
}

export default function ItServicesSection() {
  const size = 460;

  return (
    <div style={styles.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500&display=swap');
        @keyframes ringSpin { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes bgDrift { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes hubGlow { 0%,100% { opacity: 0.5; } 50% { opacity: 0.9; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: translateY(0);} }
        @media (max-width: 820px) {
          .svc-grid { grid-template-columns: 1fr !important; }
          .svc-visual { margin: 0 auto 36px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .orbit-scene * { animation: none !important; }
        }
      `}</style>

      <div className="svc-grid" style={styles.grid}>
        <div className="svc-visual orbit-scene" style={{ ...styles.scene, width: size, height: size }}>
          {/* decorative background arcs + dots, drifting very slowly as one unit */}
          <div style={{ position: "absolute", inset: 0, transformOrigin: "50% 50%", animation: "bgDrift 90s linear infinite" }}>
            <BackgroundArt size={size} />
          </div>

          {/* orbit guide rings */}
          <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" style={{ position: "absolute", inset: 0, animation: "ringSpin 46s linear infinite", transformOrigin: "50% 50%" }} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={RADIUS.inner} fill="none" stroke="rgba(245,247,247,0.16)" strokeWidth="1" strokeDasharray="2 6" />
          </svg>
          <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" style={{ position: "absolute", inset: 0, animation: "ringSpin 60s linear infinite reverse", transformOrigin: "50% 50%" }} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={RADIUS.mid} fill="none" stroke="rgba(245,247,247,0.11)" strokeWidth="1" strokeDasharray="2 7" />
          </svg>
          <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" style={{ position: "absolute", inset: 0, animation: "ringSpin 74s linear infinite", transformOrigin: "50% 50%" }} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={RADIUS.outer} fill="none" stroke="rgba(245,247,247,0.08)" strokeWidth="1" strokeDasharray="2 9" />
          </svg>

          <div style={styles.hub}>
            <div style={{ ...styles.hubGlow, animation: "hubGlow 3.2s ease-in-out infinite" }} />
            <Glyph type="server" color="#F5F7F7" />
          </div>

          {ICONS.map((icon, i) => (
            <OrbitIcon key={`${icon.ring}-${icon.type}`} icon={icon} index={i} />
          ))}
        </div>

        <div style={styles.textCol}>
          <h2 style={{ ...styles.h2, animation: "fadeUp 0.6s ease-out 0.15s both" }}>
            We deal with every aspect
            <br />
            of professional IT services
          </h2>

          <p style={{ ...styles.p, animation: "fadeUp 0.6s ease-out 0.32s both" }}>
            We're the technology partner enterprises and ambitious SMEs turn to
            when software needs to move the business forward, not just keep it
            running. From custom development to product design, our teams turn
            complex requirements into systems your people can actually rely on.
          </p>

          <p style={{ ...styles.p, animation: "fadeUp 0.6s ease-out 0.48s both" }}>
            Every engagement starts with your workflow, not your tech stack.
            That's how we build infrastructure, applications, and platforms
            that hold up as you scale not just on launch day.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    width: "100%",
    background: "linear-gradient(45deg, #11359e, #01494d)",
    color: "#ffffff",
    fontFamily: "'Inter', sans-serif",
    padding: "72px 56px",
    borderRadius: 16,
    boxSizing: "border-box",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "0.95fr 1.05fr",
    gap: 64,
    alignItems: "center",
    maxWidth: 1180,
    margin: "0 auto",
  },
  scene: { position: "relative" },
  hub: {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 92, height: 92, borderRadius: "50%", background: NAVY,
    border: "1.5px solid rgba(10,177,185,0.4)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 2,
  },
  hubGlow: { position: "absolute", inset: -14, borderRadius: "50%", background: "radial-gradient(circle, rgba(10,177,185,0.25), transparent 70%)", zIndex: -1 },
  textCol: { display: "flex", flexDirection: "column" },
  h2: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "clamp(26px, 3vw, 38px)",
    lineHeight: 1.2, letterSpacing: "-0.01em", margin: "0 0 22px",
  },
  p: { fontSize: 15.5, lineHeight: 1.7, color: "#effdfd", margin: "0 0 18px", maxWidth: 540 },
};